import type { ReactNode } from 'react'

/**
 * The parts every sub-flow sheet is built from. Six flows, one shell, six
 * shapes — composed from these rather than written six times.
 */

/**
 * What you are agreeing to, stated plainly before you agree to it. Used by
 * every sheet that ends in a commitment: turning two-factor off, cancelling a
 * plan, deleting an account.
 */
export function FactsList({ children }: { children: ReactNode }) {
  return (
    <ul
      style={{
        margin: 0,
        padding: 0,
        listStyle: 'none',
        borderRadius: 11,
        background: 'var(--w-sunk)',
        overflow: 'hidden',
      }}
    >
      {children}
    </ul>
  )
}

export function Fact({ children }: { children: ReactNode }) {
  return (
    <li
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '11px 13px',
        font: '500 12px/1.6 Manrope,sans-serif',
        textWrap: 'pretty',
      }}
    >
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: 3,
          background: 'var(--color-accent)',
          marginTop: 7,
          flex: 'none',
        }}
      />
      <span style={{ flex: 1, minWidth: 0 }}>{children}</span>
    </li>
  )
}

interface RadioOptionProps {
  on: boolean
  onPick: () => void
  label: string
  /** Right-aligned, 11px muted — a price, a consequence, a count. */
  note?: ReactNode
  /** A second line under the label, for an option that needs explaining. */
  detail?: ReactNode
  name: string
  /**
   * What this option posts under `name` when it is the one selected. Give it
   * wherever the group is inside a real form — without it the browser posts
   * "on" for a valueless radio, which is nobody's idea of a scope.
   */
  value?: string
}

/**
 * One choice in a list of them. The dot is a real ring rather than a filled
 * circle — 5px of accent border collapsing the middle — so the selected state
 * reads at a glance without a second colour anywhere on the row.
 */
export function RadioOption({ on, onPick, label, note, detail, name, value }: RadioOptionProps) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 13px',
        borderRadius: 11,
        border: on ? '1px solid var(--w-pill-line)' : '1px solid var(--w-line)',
        background: on ? 'var(--w-pane-hi)' : 'var(--w-sunk)',
        cursor: 'pointer',
      }}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={on}
        onChange={onPick}
        style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
      />
      <span
        aria-hidden="true"
        style={{
          width: 15,
          height: 15,
          borderRadius: 99,
          boxSizing: 'border-box',
          border: on ? '5px solid var(--color-accent)' : '1px solid var(--w-quiet)',
          flex: 'none',
        }}
      />
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ font: '600 12px Manrope,sans-serif' }}>{label}</span>
        {detail && (
          <span style={{ font: '400 11px/1.6 Manrope,sans-serif', color: 'var(--color-muted)' }}>{detail}</span>
        )}
      </span>
      {note && (
        <span
          style={{
            font: '400 11px Manrope,sans-serif',
            color: 'var(--color-muted)',
            whiteSpace: 'nowrap',
            flex: 'none',
          }}
        >
          {note}
        </span>
      )}
    </label>
  )
}

/**
 * A code to copy, in a dashed frame. Two-factor's secret, an API key, an
 * inbox address.
 */
export function CodeBlock({ value, onCopy }: { value: string; onCopy?: () => void }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '13px 14px',
        borderRadius: 11,
        border: '1px dashed var(--w-quiet)',
        background: 'var(--w-sunk)',
        flexWrap: 'wrap',
      }}
    >
      <span
        className="text-data"
        style={{
          flex: 1,
          minWidth: 140,
          fontSize: 13,
          color: 'var(--color-amber-ink)',
          overflowWrap: 'anywhere',
        }}
      >
        {value}
      </span>
      <button
        type="button"
        className="tab"
        onClick={() => {
          void navigator.clipboard?.writeText(value)
          onCopy?.()
        }}
        style={{
          border: 0,
          background: 'none',
          padding: 0,
          font: '600 11px Manrope,sans-serif',
          color: 'var(--color-action-ink)',
          cursor: 'pointer',
          flex: 'none',
        }}
      >
        Copy
      </button>
    </div>
  )
}
